import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Group, Loader, Paper, Progress, ScrollArea, SegmentedControl, SimpleGrid, Stack, Table, Text, Title, Tooltip,
} from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import { IconRefresh, IconSparkles } from '@tabler/icons-react';
import Link from 'next/link';
import dayjs from 'dayjs';
import * as Enums from '@/utils/enums';
import ImportDraftService, { errorMessage, isApiError } from '@/services/import-draft/import-draft-service';
import { ImportDraft } from '@/services/import-draft/import-draft-model';
import {
  RunStatusFilter, formatDuration, formatFileSize, importTypeName, isProcessing, modeLabel, progressPercent, runStatusGroup, runTotals, stageColour, stageLabel,
} from '@/services/import-draft/import-draft-helpers';

const POLL_MS = 5000;
const RUN_LIMIT = 100;

const FILTERS: { label: string; value: RunStatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'In progress', value: 'active' },
  { label: 'Ready for review', value: 'ready' },
  { label: 'Imported', value: 'imported' },
  { label: 'Failed', value: 'failed' },
];

function shortModel(model?: string | null): string {
  if (!model) return '-';
  const slash = model.lastIndexOf('/');
  return slash >= 0 ? model.slice(slash + 1) : model;
}

function canOpen(draft: ImportDraft): boolean {
  return draft.Stage !== Enums.ImportDraftStage.Cancelled;
}

export default function ImportRunsDashboard() {
  const [runs, setRuns] = useState<ImportDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<RunStatusFilter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    const result = await ImportDraftService.list(RUN_LIMIT);
    setLoading(false);
    if (isApiError(result)) {
      setError(errorMessage(result, 'Smart import runs could not be loaded.'));
      return;
    }
    setError(null);
    setRuns(result as ImportDraft[]);
  }, []);

  useEffect(() => { load(); }, [load]);

  const anyActive = !!runs?.some(r => isProcessing(r.Stage));
  const interval = useInterval(() => { load(); }, POLL_MS);
  useEffect(() => {
    if (anyActive) interval.start();
    else interval.stop();
    return interval.stop;
  }, [anyActive]);

  const totals = useMemo(() => runTotals(runs || []), [runs]);
  const visible = useMemo(
    () => (runs || []).filter(r => filter === 'all' || runStatusGroup(r.Stage) === filter),
    [runs, filter],
  );

  return (
    <Stack gap="md" data-testid="import-runs-dashboard">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>Smart import runs</Title>
          <Text size="sm" c="dimmed">The most recent {RUN_LIMIT} files uploaded through smart import.</Text>
        </div>
        <Group gap="xs">
          <Tooltip label="Refresh">
            <ActionIcon variant="default" size="lg" onClick={load} loading={loading} aria-label="Refresh smart import runs">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button component={Link} href="/settings/import/smart" leftSection={<IconSparkles size={14} />} variant="light">
            New smart import
          </Button>
        </Group>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {runs === null && !error && <Group justify="center" p="md"><Loader size="sm" /></Group>}

      {runs !== null && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
            <Stat label="Runs" value={totals.runs.toLocaleString()} />
            <Stat label="In progress" value={totals.active.toLocaleString()} tone={totals.active ? 'blue' : undefined} />
            <Stat label="Imported" value={totals.imported.toLocaleString()} tone={totals.imported ? 'green' : undefined} />
            <Stat label="Failed" value={totals.failed.toLocaleString()} tone={totals.failed ? 'red' : undefined} />
            <Stat label="Records imported" value={totals.recordsImported.toLocaleString()} />
            <Stat label="Median processing time" value={formatDuration(totals.medianDurationMs)} />
          </SimpleGrid>

          <SegmentedControl size="xs" value={filter} onChange={value => setFilter(value as RunStatusFilter)} data={FILTERS} w="fit-content" />

          <ScrollArea type="auto" offsetScrollbars w="100%">
            <Table verticalSpacing="xs" fz="sm" withTableBorder striped highlightOnHover miw={980}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>File</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Layout</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Records</Table.Th>
                  <Table.Th ta="right">Processing time</Table.Th>
                  <Table.Th>Model</Table.Th>
                  <Table.Th>Started</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visible.map(run => <RunRow key={run.ID} run={run} />)}
                {visible.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={9}>
                      <Text size="sm" c="dimmed" ta="center" p="md">
                        {runs.length === 0 ? 'No files have been uploaded through smart import yet.' : 'No runs match this filter.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </>
      )}
    </Stack>
  );
}

function RunRow({ run }: { run: ImportDraft }) {
  const percent = run.Stage === Enums.ImportDraftStage.Extracting ? progressPercent(run) : null;
  const tokens = run.InputTokens + run.OutputTokens;

  return (
    <Table.Tr data-testid={`import-run-${run.ID}`}>
      <Table.Td maw={260}>
        <Text size="sm" fw={500} truncate="end" title={run.SourceFileName}>{run.SourceFileName}</Text>
        <Text size="xs" c="dimmed">{formatFileSize(run.SourceFileSize)}</Text>
      </Table.Td>
      <Table.Td>{importTypeName(run.ImportType)}</Table.Td>
      <Table.Td>{modeLabel(run)}</Table.Td>
      <Table.Td>
        <Stack gap={4}>
          {run.Stage === Enums.ImportDraftStage.Failed && run.Error ? (
            <Tooltip label={run.Error} multiline w={320}>
              <Badge color={stageColour(run.Stage)} variant="light">{stageLabel(run.Stage)}</Badge>
            </Tooltip>
          ) : (
            <Badge color={stageColour(run.Stage)} variant="light">{stageLabel(run.Stage)}</Badge>
          )}
          {percent !== null && <Progress value={percent} size="sm" w={120} aria-label="Extraction progress" />}
        </Stack>
      </Table.Td>
      <Table.Td ta="right">
        <Text size="sm">{run.RowCount ? run.RowCount.toLocaleString() : '-'}</Text>
        {run.IssueRowCount > 0 && <Text size="xs" c="yellow.8">{run.IssueRowCount.toLocaleString()} with issues</Text>}
      </Table.Td>
      <Table.Td ta="right">{formatDuration(run.DurationMs)}</Table.Td>
      <Table.Td>
        {tokens > 0
          ? <Tooltip label={`${tokens.toLocaleString()} tokens`}><Text size="sm">{shortModel(run.Model)}</Text></Tooltip>
          : <Text size="sm" c="dimmed">{run.RecipeSource === Enums.ImportRecipeSource.Saved ? 'None (saved)' : '-'}</Text>}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{dayjs(run.CreatedDate).format('D MMM YYYY, HH:mm')}</Text>
        {run.CreatedBy && <Text size="xs" c="dimmed">{run.CreatedBy}</Text>}
      </Table.Td>
      <Table.Td>
        {canOpen(run) && (
          <Button component={Link} href={`/settings/import/smart?draftId=${run.ID}`} size="xs" variant="subtle">
            {run.Stage === Enums.ImportDraftStage.Committed ? 'View' : 'Open'}
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={700} size="lg" c={tone}>{value}</Text>
    </Paper>
  );
}
